import { create } from "zustand";
import { toSupportedLanguage, type SupportedLanguage, type Toast } from "@/lib/types";
import { generateId } from "@/lib/utils";
import { LIMITS } from "@/lib/constants";

// ─── Read persisted values synchronously so the store never starts
//     with wrong defaults (avoids flash + provider effect race).
function getStoredTheme(): "light" | "dark" {
  try {
    const v = localStorage.getItem("wexare-theme");
    if (v === "dark" || v === "light") return v;
  } catch {
    /* SSR / restricted storage */
  }
  return typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/**
 * The initial language AND where it came from.
 *
 * `explicit` separates "this member chose Portuguese" from "this browser is
 * set to Portuguese". They used to be the same value, and once the profile
 * column became authoritative for outbound email that conflation would have
 * written a browser guess as a durable preference for every existing member --
 * permanently outranking the country inference that had been deciding their
 * mail. A guess is not a choice, so only a stored value counts.
 *
 * Read once at store creation, deliberately: LanguageProvider writes the key
 * on every later change (hydration included), so the storage slot stops being
 * evidence of a CHOICE the moment the app starts running.
 */
function readStoredLanguage(): { language: SupportedLanguage; explicit: boolean } {
  try {
    const stored = toSupportedLanguage(localStorage.getItem("wexare-language"));
    if (stored) return { language: stored, explicit: true };
    const browser = toSupportedLanguage(navigator.language.split("-")[0]);
    if (browser) return { language: browser, explicit: false };
  } catch {
    /* SSR / restricted storage */
  }
  return { language: "en", explicit: false };
}

const initialLanguage = readStoredLanguage();

const SIDEBAR_WIDTH_KEY = "wexare-sidebar-width";
const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 480;
const SIDEBAR_DEFAULT_WIDTH = 240;

function getStoredSidebarWidth(): number {
  try {
    const v = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    if (v) {
      const n = Number(v);
      if (Number.isFinite(n) && n >= SIDEBAR_MIN_WIDTH && n <= SIDEBAR_MAX_WIDTH) {
        return n;
      }
    }
  } catch {
    /* SSR / restricted storage */
  }
  return SIDEBAR_DEFAULT_WIDTH;
}

const CELEBRATION_SOUND_KEY = "wexare-celebration-sound";

/** Off unless the member turned it on — a surprise sound on a phone is worse than none. */
function getStoredCelebrationSound(): boolean {
  try {
    return localStorage.getItem(CELEBRATION_SOUND_KEY) === "1";
  } catch {
    return false;
  }
}

export const SIDEBAR_LIMITS = {
  MIN: SIDEBAR_MIN_WIDTH,
  MAX: SIDEBAR_MAX_WIDTH,
  DEFAULT: SIDEBAR_DEFAULT_WIDTH,
} as const;

interface UIStore {
  sidebarOpen: boolean;
  sidebarWidth: number;
  theme: "light" | "dark";
  language: SupportedLanguage;
  toasts: Toast[];
  chunkRecoveryFailed: boolean;
  /** Celebration chime (confetti follows the OS reduced-motion setting instead). */
  celebrationSound: boolean;
  setCelebrationSound: (on: boolean) => void;
  toggleSidebar: () => void;
  setSidebarWidth: (width: number) => void;
  setTheme: (theme: "light" | "dark") => void;
  /** False while `language` is only a browser/default guess, never a choice. */
  languageIsExplicit: boolean;
  setLanguage: (language: SupportedLanguage) => void;
  addToast: (toast: Omit<Toast, "id">) => void;
  removeToast: (id: string) => void;
  markChunkRecoveryFailed: () => void;
}

export const useUIStore = create<UIStore>((set) => ({
  sidebarOpen: false,
  sidebarWidth: getStoredSidebarWidth(),
  theme: getStoredTheme(),
  language: initialLanguage.language,
  languageIsExplicit: initialLanguage.explicit,
  toasts: [],
  chunkRecoveryFailed: false,
  celebrationSound: getStoredCelebrationSound(),

  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),

  setSidebarWidth: (width) => {
    const clamped = Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, Math.round(width)));
    try {
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(clamped));
    } catch {
      /* storage unavailable */
    }
    set({ sidebarWidth: clamped });
  },

  setTheme: (theme) => set({ theme }),

  // Any call is a deliberate determination -- the switcher, a cross-tab
  // storage event, or hydration from the member's own profile -- so it
  // promotes the value out of "guessed".
  setLanguage: (language) => set({ language, languageIsExplicit: true }),

  addToast: (toast) => {
    const id = generateId();
    set((s) => ({ toasts: [...s.toasts, { ...toast, id }] }));
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    }, toast.durationMs ?? LIMITS.TOAST_DURATION_MS);
  },

  removeToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  markChunkRecoveryFailed: () => set({ chunkRecoveryFailed: true }),

  setCelebrationSound: (on) => {
    try {
      localStorage.setItem(CELEBRATION_SOUND_KEY, on ? "1" : "0");
    } catch {
      /* restricted storage — still applies for this session */
    }
    set({ celebrationSound: on });
  },
}));
